/**
 * Shared intake note-section definitions and the pediatric age helper.
 *
 * The call guide (`IntakeGuideSection`, where per-section scratch is captured)
 * and the Notes step (`ClinicalNotesFields`, where scratch is surfaced on
 * hover) both import this list so scratch keys line up across the two surfaces.
 * @agentic connections
 */

/**
 * A note section: a header label and the ordered subsection field keys it
 * contains. `pediatric` sections render only for patients under 18 (D10).
 * `audience` marks a section whose relevance depends on the patient's age so
 * the call guide can chip it ("adult" / "pediatric") next to the header.
 * @agentic connections
 */
export type NoteSection = {
  id: string;
  label: string;
  fieldKeys: string[];
  /**
   * Generic fields this section's subsections replaced. They are no longer
   * authored — not drafted, not prefilled, not offered as a call-guide topic —
   * but encounters documented before the split still carry them, so the Notes
   * step renders them read-only when a value is present. Clearing one would
   * emit a DELETE for the historical ClinicalImpression, hence read-only.
   */
  legacyFieldKeys?: string[];
  pediatric?: boolean;
  audience?: "adult" | "pediatric";
};

/**
 * Note sections in display order (PRD §8.3). Every clinical-impression field
 * key (except `comment`, handled separately) belongs to exactly one section,
 * as either an authored `fieldKeys` entry or a retired `legacyFieldKeys` one.
 *
 * Section `id`s are also the keys for per-section scratch notes, so they must
 * not be renamed even when they match a retired field key — a saved draft's
 * scratch is stored under the old id.
 * @agentic connections
 */
export const NOTE_SECTIONS: NoteSection[] = [
  {
    id: "chief-complaint",
    label: "Chief Complaint",
    fieldKeys: ["chief-complaint"],
  },
  {
    id: "hpi",
    label: "History of Present Illness",
    fieldKeys: ["hpi-current-symptoms"],
  },
  {
    id: "mental-health-history",
    label: "Mental Health History",
    fieldKeys: [
      "mental-health-history",
      "trauma-history",
      "hospitalizations",
      "safety",
    ],
  },
  {
    id: "current-outpatient-treaters",
    label: "Current Outpatient Clinicians",
    fieldKeys: [
      "existing-treaters",
      "outpatient-care-history",
      "medication-history",
    ],
    legacyFieldKeys: ["current-outpatient-treaters"],
  },
  {
    id: "substance-use-history",
    label: "Substance Use History",
    fieldKeys: ["substance-current-use", "substance-treatment-history"],
    legacyFieldKeys: ["substance-use-history"],
  },
  {
    id: "medical-history",
    label: "Medical History",
    fieldKeys: ["chronic-conditions", "other-physical-symptoms"],
    legacyFieldKeys: ["medical-history"],
  },
  {
    id: "mental-status-exam",
    label: "Mental Status Examination",
    fieldKeys: [
      "mental-status-appearance",
      "mental-status-behavior",
      "mental-status-speech",
      "mental-status-mood-affect",
      "mental-status-thought-process-content",
      "mental-status-perception",
      "mental-status-cognition",
      "mental-status-insight-judgment",
    ],
  },
  {
    id: "social-history",
    label: "Social History",
    fieldKeys: [
      "family-history",
      "living-situation",
      "social-supports",
      "occupation-education",
    ],
    legacyFieldKeys: ["social-history"],
    audience: "adult",
  },
  {
    id: "pediatric-social",
    label: "Social History",
    fieldKeys: [
      "peds-home-life",
      "peds-school-education",
      "peds-activities-strengths",
      "peds-peers-social",
      "peds-family-history",
    ],
    pediatric: true,
    audience: "pediatric",
  },
  {
    id: "pediatric-birth-dev",
    label: "Birth & Development",
    fieldKeys: ["peds-birth-complications", "peds-developmental-milestones"],
    pediatric: true,
    audience: "pediatric",
  },
  {
    id: "assessment-plan",
    label: "Assessment and Plan",
    fieldKeys: ["assessment-plan"],
  },
];

/**
 * Whether the patient is under 18 as of `referenceDate` (defaults to today).
 * Returns false when the birth date is missing or unparseable — pediatric
 * content stays hidden unless we can positively determine the patient is a
 * minor.
 * @agentic connections
 */
export const isUnderEighteen = (
  birthDate: string | undefined,
  referenceDate: string | undefined,
): boolean => {
  if (!birthDate) return false;
  // Parse the FHIR `birthDate` (YYYY-MM-DD) as calendar parts to avoid the
  // UTC-parse-vs-local-getter off-by-one around the 18th birthday.
  const [by, bm, bd] = birthDate.split("-").map((n) => parseInt(n, 10));
  if (!by || !bm || !bd) return false;
  // Prefer the reference's calendar-date components. When it's an ISO string
  // (e.g. an encounter start with a timezone offset), read the leading
  // YYYY-MM-DD directly rather than converting to UTC — the latter can cross
  // midnight and misclassify the 18th birthday.
  let ry: number, rm: number, rd: number;
  const isoDate = referenceDate?.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoDate) {
    ry = parseInt(isoDate[1], 10);
    rm = parseInt(isoDate[2], 10);
    rd = parseInt(isoDate[3], 10);
  } else {
    const now = new Date();
    ry = now.getFullYear();
    rm = now.getMonth() + 1;
    rd = now.getDate();
  }
  let age = ry - by;
  if (rm < bm || (rm === bm && rd < bd)) age -= 1;
  return age < 18;
};
